import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext();

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // null = checking, false = not auth'd, object = auth'd
  const [loading, setLoading] = useState(true);

  // Check existing session on mount
  useEffect(() => {
    const token = sessionStorage.getItem('user_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setUser({ ...res.data, token }))
      .catch(() => { sessionStorage.removeItem('user_token'); setUser(false); })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem('user_token', data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const register = useCallback(async (formData) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, formData);
      sessionStorage.setItem('user_token', data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('user_token');
    setUser(false);
    axios.post(`${API}/auth/logout`).catch(() => {});
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = sessionStorage.getItem('user_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const saveData = useCallback(async (appData) => {
    try {
      await axios.post(`${API}/user/data`, appData, { headers: getAuthHeaders() });
      return true;
    } catch {
      return false;
    }
  }, [getAuthHeaders]);

  const loadData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/user/data`, { headers: getAuthHeaders() });
      return data.data;
    } catch {
      return null;
    }
  }, [getAuthHeaders]);

  const refreshUser = useCallback(async () => {
    const token = sessionStorage.getItem('user_token');
    if (!token) return;
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser({ ...res.data, token });
    } catch { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, saveData, loadData, getAuthHeaders, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
